// Year review (admin). The first screen that is not month-scoped: how many hours the year actually
// contained, how the headcount moved, and where absence concentrated.
//
// Two things it is careful about, because both would otherwise mislead:
//   • Months before tracking began, and months still in the future, are shown but excluded from
//     every total and average. The year is reported AS OF today, not as if December had happened.
//   • Office hours are assumed equal to the planned shift while warehouse hours are measured from
//     scan activity, so the two are not comparable. That is stated on the page, not buried.
import React, { useCallback, useEffect, useState } from 'react';
import { authFetch, API } from './api';
import HelpPanel from './HelpPanel';

interface Month {
  month: number; headcount: number; fte: number | null; future: boolean; partial: boolean;
  pre_tracking: boolean;
  expected_min: number; worked_min: number; vacation_min: number; haigusleht_min: number;
  elsewhere_min: number; holiday_worked_min: number; excess_min: number; deficit_min: number;
  net_min: number; expected_days: number; counted_days: number; missed_days: number;
  paid_absence_days: number; extra_days: number;
}
interface Person {
  username: string; display_name: string; worker_type: string; contract_type: string;
  months_employed: number; expected_min: number; worked_min: number;
  expected_days: number; counted_days: number; missed_days: number; extra_days: number;
  reliability: number | null; commitment: number | null;
  absence_spells: number; absence_days: number; bradford: number; no_shows: number;
  vacation_entitled: number; vacation_taken: number; vacation_left: number;
}
interface Data {
  year: number; as_of: string; complete: boolean; tracking_start: string | null;
  months: Month[]; people: Person[]; totals: any;
  absence_days_by_type: Record<string, number>;
  type_labels: Record<string, string>; bradford_types: string[];
}

const inp = 'bg-gray-800 border border-gray-700 text-gray-200 text-xs rounded px-2 py-1 focus:outline-none focus:border-blue-500';
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const h = (m: number | null) => ((m || 0) / 60).toFixed(1);
const dash = (v: any) => (v === null || v === undefined || v === 0 ? '—' : v);

export default function AnnualPanel() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [d, setD] = useState<Data | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [tStart, setTStart] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    const r = await authFetch(`${API}/statistics/work-time/annual?year=${year}`);
    setLoading(false);
    if (!r.ok) { setErr('Could not build the year review'); return; }
    const data: Data = await r.json();
    setD(data);
    setTStart(data.tracking_start || '');
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const saveTracking = async () => {
    const r = await authFetch(`${API}/statistics/work-time/settings`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tracking_start_date: tStart }),
    });
    if (!r.ok) { setErr('Could not save the tracking start date'); return; }
    setMsg('Tracking start saved — the review below now ignores anything before it.');
    load();
  };

  const download = () => {
    const token = localStorage.getItem('wms_token') || '';
    // A plain link cannot carry the auth header, so fetch as a blob and hand it to the browser.
    fetch(`${API}/statistics/work-time/annual-export?year=${year}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(async res => {
      if (!res.ok) { setErr('Export failed'); return; }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `year-review-${year}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    });
  };

  const t = d?.totals;
  const shown = (m: Month) => !m.future && !m.pre_tracking;

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="text-xs text-gray-500">Year
          <input type="number" min={2020} max={2100} className={`${inp} ml-2 w-20`} value={year}
            onChange={e => setYear(parseInt(e.target.value) || year)} />
        </label>
        <label className="text-xs text-gray-500">Tracking starts
          <input type="date" className={`${inp} ml-2`} value={tStart}
            onChange={e => setTStart(e.target.value)} />
        </label>
        <button onClick={saveTracking}
          className="px-2.5 py-1 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs rounded-lg">
          Save
        </button>
        {d && (
          <span className="text-xs text-gray-600">
            as of {d.as_of}{d.complete ? ' · year complete' : ' · year in progress'}
          </span>
        )}
        <button onClick={download}
          className="ml-auto px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg font-medium">
          Download year review (xlsx)
        </button>
      </div>

      {err && <div className="mb-3 bg-red-950/40 border border-red-900/60 text-red-300 text-xs rounded-lg px-3 py-2">{err}</div>}
      {msg && <div className="mb-3 bg-[#1a1d27] border border-gray-800 text-xs text-gray-400 rounded-lg px-3 py-2">{msg}</div>}
      {loading && <div className="text-xs text-gray-500 mb-3">Building…</div>}

      {/* The caveat belongs on the page, not in a footnote. */}
      <div className="mb-4 bg-[#1a1d27] border border-amber-900/40 rounded-xl px-4 py-3">
        <div className="text-amber-400/90 text-xs uppercase tracking-widest font-semibold mb-1">
          Read these numbers with care
        </div>
        <p className="text-xs text-gray-500">
          Office hours are <span className="text-gray-300">assumed</span> to equal the planned shift,
          while warehouse hours are <span className="text-gray-300">measured</span> from scanning
          activity — so office people score near 100% by construction and warehouse people score low
          wherever work happened offline or in another system. Do not compare the two groups directly,
          and correct warehouse days in the Calendar rather than reading them as absence.
        </p>
      </div>

      {t && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {[
            ['Expected', `${h(t.expected_min)} h`, 'Contracted hours since tracking began'],
            ['Worked', `${h(t.worked_min)} h`, 'Includes business trips and training'],
            ['Days counted', `${t.counted_days} / ${t.expected_days}`, `${t.missed_days} missed`],
            ['Average FTE', `${t.avg_fte}`, `${t.months_counted} whole month(s) · peak headcount ${t.peak_headcount}`],
          ].map(([label, value, sub]) => (
            <div key={label as string} className="bg-[#1a1d27] border border-gray-800 rounded-xl px-4 py-3">
              <div className="text-[11px] text-gray-500 uppercase tracking-wide">{label}</div>
              <div className="text-white text-xl font-semibold mt-0.5">{value}</div>
              <div className="text-[11px] text-gray-600 mt-0.5">{sub}</div>
            </div>
          ))}
        </div>
      )}

      {/* By month */}
      <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-2 px-1">By month</div>
      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-x-auto mb-5">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-500 text-xs border-b border-gray-800">
              <th className="px-3 py-2 text-left font-medium">Month</th>
              <th className="px-3 py-2 text-center font-medium">Heads</th>
              <th className="px-3 py-2 text-center font-medium" title="Total expected hours ÷ one full-time person">FTE</th>
              <th className="px-3 py-2 text-right font-medium">Expected</th>
              <th className="px-3 py-2 text-right font-medium">Worked</th>
              <th className="px-3 py-2 text-right font-medium">Vacation</th>
              <th className="px-3 py-2 text-right font-medium">Sick</th>
              <th className="px-3 py-2 text-right font-medium">Net</th>
              <th className="px-3 py-2 text-center font-medium">Days</th>
              <th className="px-3 py-2 text-center font-medium">Missed</th>
            </tr>
          </thead>
          <tbody>
            {(d?.months || []).map(m => (
              <tr key={m.month} className={`border-b border-gray-800/50 ${shown(m) ? '' : 'opacity-40'}`}>
                <td className="px-3 py-2 text-gray-300">
                  {MON[m.month - 1]}
                  {m.pre_tracking && <span className="ml-2 text-[10px] text-gray-600 uppercase">pre-tracking</span>}
                  {m.future && <span className="ml-2 text-[10px] text-gray-600 uppercase">future</span>}
                  {m.partial && <span className="ml-2 text-[10px] text-blue-400/70 uppercase">to date</span>}
                </td>
                <td className="px-3 py-2 text-center text-gray-400">{m.headcount || '—'}</td>
                <td className="px-3 py-2 text-center text-gray-300">{m.fte ?? '—'}</td>
                <td className="px-3 py-2 text-right text-gray-400">{shown(m) ? `${h(m.expected_min)} h` : '—'}</td>
                <td className="px-3 py-2 text-right text-gray-300">{shown(m) ? `${h(m.worked_min)} h` : '—'}</td>
                <td className="px-3 py-2 text-right text-gray-500">{m.vacation_min ? `${h(m.vacation_min)} h` : '—'}</td>
                <td className="px-3 py-2 text-right text-gray-500">{m.haigusleht_min ? `${h(m.haigusleht_min)} h` : '—'}</td>
                <td className={`px-3 py-2 text-right ${m.net_min < 0 ? 'text-red-400' : m.net_min > 0 ? 'text-green-400' : 'text-gray-600'}`}>
                  {shown(m) ? `${h(m.net_min)} h` : '—'}
                </td>
                <td className="px-3 py-2 text-center text-gray-400">
                  {shown(m) ? `${m.counted_days}/${m.expected_days}` : '—'}
                </td>
                <td className="px-3 py-2 text-center">
                  {m.missed_days > 0 ? <span className="text-red-400">{m.missed_days}</span> : <span className="text-gray-700">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* By person */}
      <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-2 px-1">By person</div>
      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-x-auto mb-5">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-500 text-xs border-b border-gray-800">
              <th className="px-3 py-2 text-left font-medium">Person</th>
              <th className="px-3 py-2 text-left font-medium">Type</th>
              <th className="px-3 py-2 text-right font-medium">Expected</th>
              <th className="px-3 py-2 text-right font-medium">Worked</th>
              <th className="px-3 py-2 text-center font-medium">Days</th>
              <th className="px-3 py-2 text-center font-medium" title="Counted ÷ days they were expected and not on paid leave">Reliab.</th>
              <th className="px-3 py-2 text-center font-medium" title="Separate absence spells / total absence days">Spells</th>
              <th className="px-3 py-2 text-center font-medium" title="Spells² × days — frequent short absences score much higher">Bradford</th>
              <th className="px-3 py-2 text-center font-medium">No-shows</th>
              <th className="px-3 py-2 text-center font-medium" title="Taken / entitled for the year">Vacation</th>
            </tr>
          </thead>
          <tbody>
            {(d?.people || []).map(p => (
              <tr key={p.username} className="border-b border-gray-800/50">
                <td className="px-3 py-2 text-gray-200">{p.display_name}</td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {p.worker_type === 'office' ? 'Office' : 'Warehouse'}
                  {p.worker_type === 'office' && (
                    <span className="ml-1 text-[10px] text-gray-600" title="Hours assumed from the planned shift">assumed</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right text-gray-400">{h(p.expected_min)} h</td>
                <td className="px-3 py-2 text-right text-gray-300">{h(p.worked_min)} h</td>
                <td className="px-3 py-2 text-center text-gray-400">{p.counted_days}/{p.expected_days}</td>
                <td className={`px-3 py-2 text-center ${p.reliability === null ? 'text-gray-700' : p.reliability >= 0.95 ? 'text-green-400' : p.reliability >= 0.7 ? 'text-gray-300' : 'text-amber-400'}`}>
                  {p.reliability === null ? '—' : `${Math.round(p.reliability * 100)}%`}
                </td>
                <td className="px-3 py-2 text-center text-gray-400">
                  {p.absence_spells ? `${p.absence_spells} / ${p.absence_days}d` : '—'}
                </td>
                <td className={`px-3 py-2 text-center ${p.bradford >= 50 ? 'text-red-400 font-medium' : p.bradford > 0 ? 'text-gray-300' : 'text-gray-700'}`}>
                  {dash(p.bradford)}
                </td>
                <td className="px-3 py-2 text-center">
                  {p.no_shows > 0 ? <span className="text-red-400 font-medium">{p.no_shows}</span> : <span className="text-gray-700">—</span>}
                </td>
                <td className="px-3 py-2 text-center text-gray-400">
                  {p.vacation_taken} / {Math.round(p.vacation_entitled * 10) / 10}
                </td>
              </tr>
            ))}
            {(d?.people || []).length === 0 && !loading && (
              <tr><td colSpan={10} className="px-3 py-6 text-center text-gray-600 text-sm">
                Nobody was employed in {year} after tracking began.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Absence by type */}
      {d && Object.keys(d.absence_days_by_type).length > 0 && (
        <>
          <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-2 px-1">
            Absence days by type
          </div>
          <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-4 mb-5">
            <div className="flex flex-wrap gap-x-6 gap-y-1.5">
              {Object.entries(d.absence_days_by_type).map(([k, v]) => (
                <div key={k} className="text-xs">
                  <span className="text-gray-500">{d.type_labels[k] || k}:</span>
                  <span className="text-gray-200 ml-1.5 font-medium">{v}</span>
                  <span className="text-gray-600"> d</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-gray-600 mt-2.5 pt-2.5 border-t border-gray-800/60">
              Bradford counts only unplanned absence ({(d.bradford_types || []).map(x => d.type_labels[x] || x).join(', ')}).
              Planned leave never scores against anyone.
            </p>
          </div>
        </>
      )}

      <HelpPanel items={[
        { label: 'Tracking starts', desc: 'The date real time-tracking began. Months before it are shown greyed as "pre-tracking" and left out of every total, average and FTE figure — nothing is deleted, it simply stays out of the review. Set it once per system migration.' },
        { label: 'As of / to date / future', desc: 'The year is reported as of today. The current month is marked "to date" and future months are blank, so a year in progress never claims hours that have not happened. Part months are also excluded from the FTE and headcount averages, since they would understate both.' },
        { label: 'FTE', desc: 'Total expected hours divided by what one full-time Mon–Fri person owes that month, following public holidays. Three people on half weeks read as 1.5, where a headcount would say 3.' },
        { label: 'Reliability', desc: 'Days that counted, divided by days they were expected and not on approved paid leave — so vacation never reads as unreliability. Remember office hours are assumed, which pins office staff near 100%.' },
        { label: 'Spells / Bradford', desc: 'Spells is how many separate absences, with total days after the slash. Bradford is spells² × days: five separate one-day absences score 125, one five-day absence scores 5. It highlights disruptive patterns a day-count hides — a prompt for a conversation, not a verdict.' },
        { label: 'No-shows', desc: 'Absences where nobody told us. Only counts spells recorded through the Absences screen — days entered before that screen existed have no notification record.' },
        { label: 'Vacation taken / entitled', desc: 'Entitled is pro-rata accrual for the full year plus anything carried in. If Taken looks too low, historic vacation still needs booking — see the backfill on the Vacation tab.' },
        { label: 'Download year review', desc: 'The same figures as three sheets: by month, by person, and absence by type. Separate from the monthly accountant hand-off, which is unchanged.' },
      ]} />
    </div>
  );
}
